import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryService, typeService } from 'shared/services';
import { Option } from 'shared/types/general';
import { IProduct } from '../../types';
import { SectionHeader } from 'components/ui/typography';

interface ProductFormParams {
  product: IProduct;
  onSubmit: any;
  disableEdit?: boolean;
}

export const ProductForm = ({ product, onSubmit, disableEdit }: ProductFormParams) => {
  const translationPrefix = 'entities.product';
  const { t } = useTranslation();

  const [categories, setCategories] = useState<Option[]>([]);
  const [types, setTypes] = useState<Option[]>([]);

  useEffect(() => {
    categoryService.getAllBaseInformation().then(setCategories);
    typeService.getAllBaseInformation().then(setTypes);
  }, []);

  return (
    <>
      {product && (
        <EnhancedForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!product.id}
          disableEdit={disableEdit}
          autoStyleLayout={true}
          columns={12}>
          <SectionHeader title={t('global.base-info-form')} gridSpan />
          <IdentityField
            gridSpan={3}
            name="code"
            label={t(`global.code`)}
            initialValue={product.code}
            component={TextFieldAdapter}
            placeholder={t('global.code')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={3}
            name="ref"
            placeholder={t(`${translationPrefix}.ref`)}
            label={t(`${translationPrefix}.ref`)}
            initialValue={product.ref}
            component={TextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={6}
            name="description"
            label={t(`global.description`)}
            initialValue={product.description}
            component={TextFieldAdapter}
            placeholder={t('global.description')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="commercial_name"
            label={t(`entities.product.commercial_name`)}
            initialValue={product.commercial_name}
            component={TextFieldAdapter}
            placeholder={t('entities.product.commercial_name')}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="category"
            label={t(`global.category`)}
            initValue={product.category}
            component={AutocompleteAdapter}
            options={categories}
            placeholder="Category"
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="type"
            label={t(`global.type`)}
            initValue={product.type}
            component={AutocompleteAdapter}
            options={types}
            placeholder="Type"
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="manufacturer_name"
            label={t(`entities.product.manufacturer_name`)}
            initialValue={product.manufacturer_name}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="manufacturer_id"
            label={t(`entities.product.manufacturer_id`)}
            initialValue={product.manufacturer_id}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="supplier_id"
            label={t(`entities.product.supplier_id`)}
            initialValue={product.supplier_id}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="ce_regulation"
            label={t(`entities.product.ce_regulation`)}
            initialValue={product.ce_regulation}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="id_issuing_authority"
            label={t(`entities.product.id_issuing_authority`)}
            initialValue={product.id_issuing_authority}
            component={TextFieldAdapter}
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="ivdr"
            label={t(`entities.product.ivdr_class`)}
            component={TextFieldAdapter}
            initialValue={product.ivdr}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="cnd_code"
            label={t(`entities.product.cnd_code`)}
            initialValue={product.cnd_code}
            component={TextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="rdm_code"
            label={t(`entities.product.rdm_code`)}
            initialValue={product.rdm_code}
            component={TextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
          <SectionHeader title={t('entities.product.packaging')} gridSpan />
          <IdentityField
            gridSpan={4}
            name="uom"
            label={t(`entities.product.uom`)}
            component={TextFieldAdapter}
            initialValue={product.uom}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="packaging"
            label={t(`entities.product.packaging`)}
            initialValue={product.packaging}
            component={TextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="units_per_package"
            label={t(`entities.product.units_per_package`)}
            initialValue={product.units_per_package}
            component={TextFieldAdapter}
            type="number"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={4}
            name="tests_per_kit"
            label={t(`entities.product.tests_per_kit`)}
            initialValue={product.tests_per_kit}
            component={TextFieldAdapter}
            type="number"
            disabled={disableEdit}
          />
          <SectionHeader title={t('global.thresholds')} gridSpan />
          <IdentityField
            gridSpan={6}
            name="expiration_alert_threshold"
            label={t(`entities.product.expiration_alert_threshold`)}
            initialValue={product.expiration_alert_threshold}
            component={TextFieldAdapter}
            type="number"
            disabled={disableEdit}
          />
          <IdentityField
            gridSpan={6}
            name="temperature"
            label={t(`entities.product.preservation_temperature`)}
            initialValue={product.temperature}
            component={TextFieldAdapter}
            type="text"
            disabled={disableEdit}
          />
        </EnhancedForm>
      )}
    </>
  );
};
